import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CommonLayout from "../../layouts/CommonLayout";
import StaticKakaoMapContainer from '../../containers/StaticKakaoMapContainer';
import DatePickOverlay from '../../layouts/DatePickOverlay';
import TimePickOverlay from '../../layouts/TimePickOverlay';
import { createMeeting } from '../../../api/MeetingApi';
import {uploadImage, deleteImage, getImageUrl} from '../../../api/ImageApi';
import { IoCalendarOutline, IoTimeOutline, IoLocationOutline, IoAdd, IoRemove, IoChevronForward } from 'react-icons/io5';
import ImageUploadContainer from "../../containers/ImageUploadContainer";
import { AlertModal } from '../../layouts/AlertModal';
import { ConfirmModal } from '../../layouts/ConfirmModal';

const STORAGE_KEY = 'createMeetingFormData';

const CreateMeetingPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, setFormData] = useState({
        imageId: null,
        title: '',
        content: '',
        placeName: '',
        placeAddress: '',
        placePhone: '',
        placeLatitude: null,
        placeLongitude: null,
        partnerPlaceId: null,
        minCapacity: 2,
        maxCapacity: 4,
        requiresApproval: false,
    });
    const [imageUrl, setImageUrl] = useState(null);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTime, setSelectedTime] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [showTimePicker, setShowTimePicker] = useState(false);

    // 모달 상태 관리
    const [alertState, setAlertState] = useState({ isVisible: false, message: '', callback: null });
    const [confirmState, setConfirmState] = useState({ isVisible: false });

    const getErrorMessage = (error) => {
        if (error.response && error.response.data && error.response.data.message) {
            return error.response.data.message;
        }
        return '오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    };

    useEffect(() => {
        const savedData = sessionStorage.getItem(STORAGE_KEY);
        if (savedData) {
            try {
                const parsed = JSON.parse(savedData);
                setFormData(parsed.formData);
                setSelectedDate(parsed.selectedDate);
                setSelectedTime(parsed.selectedTime);

                // 이미지 ID가 있다면 이미지 URL도 복원
                if (parsed.formData.imageId && parsed.imageUrl) {
                    setImageUrl(parsed.imageUrl); // S3 URL 직접 복원
                }
            } catch (error) {
                console.error('Failed to restore form data:', error);
            }
        }

        if (location.state?.selectedPlace) {
            const place = location.state.selectedPlace;
            setFormData(prev => ({
                ...prev,
                placeName: place.name,
                placeAddress: place.address,
                placePhone: place.phone || '',
                placeLatitude: place.latitude,
                placeLongitude: place.longitude,
                partnerPlaceId: place.isPartner ? place.partnerId : null,
            }));

            // 이미지 ID가 있다면 이미지 URL 복원
            if (location.state.formData?.imageId) {
                setFormData(prev => ({
                    ...prev,
                    imageId: location.state.formData.imageId
                }));
                // S3 URL 직접 복원
                if (location.state.formData.imageUrl) {
                    setImageUrl(location.state.formData.imageUrl);
                }
            }

            window.history.replaceState({}, document.title);
        }

        // sessionStorage는 location.state 처리 후에 정리
        if (location.state?.selectedPlace) {
            sessionStorage.removeItem(STORAGE_KEY);
        }
    }, [location.state]);

    const updateFormData = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setIsLoading(true);
            if (formData.imageId) {
                await deleteImage(formData.imageId);
                setImageUrl(null);
            }
            const response = await uploadImage(file);
            updateFormData('imageId', response.id);
            setImageUrl(response.url);
        } catch (error) {
            setAlertState({ isVisible: true, message: '파일 업로드에 실패했습니다. 파일은 10MB 이하로 업로드해주세요.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleImageDelete = () => {
        updateFormData('imageId', null);
        setImageUrl(null);
    };

    const handleMinCapacityChange = (increment) => {
        const newMinCapacity = formData.minCapacity + increment;
        if (newMinCapacity >= 2 && newMinCapacity <= formData.maxCapacity) {
            updateFormData('minCapacity', newMinCapacity);
        }
    };

    const handleMaxCapacityChange = (increment) => {
        const newMaxCapacity = formData.maxCapacity + increment;
        if (newMaxCapacity >= formData.minCapacity && newMaxCapacity <= 20) {
            updateFormData('maxCapacity', newMaxCapacity);
        }
    };

    const formatDateDisplay = (dateString) => {
        if (!dateString) return '날짜 선택';
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
        const weekday = weekdays[date.getDay()];
        return `${year}년 ${month}월 ${day}일 (${weekday})`;
    };

    const formatTimeDisplay = (timeString) => {
        if (!timeString) return '시간 선택';
        const [hour, minute] = timeString.split(':').map(Number);
        const period = hour < 12 ? '오전' : '오후';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${period} ${displayHour}:${minute.toString().padStart(2, '0')}`;
    };

    const isPastDateTime = () => {
        if (!selectedDate || !selectedTime) return false;
        const [selectedHour, selectedMinute] = selectedTime.split(':').map(Number);
        const selectedDateTime = new Date(selectedDate);
        selectedDateTime.setHours(selectedHour, selectedMinute, 0, 0);
        const now = new Date();
        const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
        return selectedDateTime < koreaTime;
    };

    const isFormValid = () => {
        return !!(
            formData.title.trim() &&
            formData.content.trim() &&
            selectedDate &&
            selectedTime &&
            formData.placeName.trim() &&
            !isPastDateTime()
        );
    };

    const handleSubmit = () => {
        if (!isFormValid()) {
            setAlertState({ isVisible: true, message: '모든 필수 항목을 입력해주세요.' });
            return;
        }
        setConfirmState({ isVisible: true });
    };

    const handleConfirmSubmit = async () => {
        try {
            setIsLoading(true);
            const [selectedHour, selectedMinute] = selectedTime.split(':').map(Number);
            const dateTime = new Date(selectedDate);
            dateTime.setHours(selectedHour, selectedMinute, 0, 0);

            const year = dateTime.getFullYear();
            const month = (dateTime.getMonth() + 1).toString().padStart(2, '0');
            const day = dateTime.getDate().toString().padStart(2, '0');
            const hour = dateTime.getHours().toString().padStart(2, '0');
            const minute = dateTime.getMinutes().toString().padStart(2, '0');
            const second = dateTime.getSeconds().toString().padStart(2, '0');
            const scheduledAt = `${year}-${month}-${day}T${hour}:${minute}:${second}`;

            const meetingData = {
                imageId: formData.imageId,
                title: formData.title.trim(),
                content: formData.content.trim(),
                scheduledAt: scheduledAt,
                placeName: formData.placeName.trim(),
                placeAddress: formData.placeAddress.trim() || null,
                placePhone: formData.placePhone.trim() || null,
                placeLatitude: formData.placeLatitude,
                placeLongitude: formData.placeLongitude,
                partnerPlaceId: formData.partnerPlaceId,
                minCapacity: formData.minCapacity,
                maxCapacity: formData.maxCapacity,
                requiresApproval: formData.requiresApproval
            };

            await createMeeting(meetingData);
            setAlertState({
                isVisible: true,
                message: '모임이 생성되었습니다!',
                callback: () => navigate('/app/meetings', { replace: true })
            });
        } catch (error) {
            console.error('모임 생성 실패:', error);
            setAlertState({ isVisible: true, message: getErrorMessage(error) });
        } finally {
            setIsLoading(false);
            setConfirmState({ isVisible: false });
        }
    };

    const handlePlaceSelect = () => {
        const dataToSave = {
            formData,
            selectedDate,
            selectedTime,
            imageUrl // S3 URL도 함께 저장
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
        navigate('/app/place-selection', {
            state: formData.placeName ? {
                previousPlace: {
                    name: formData.placeName,
                    address: formData.placeAddress,
                    phone: formData.placePhone,
                    latitude: formData.placeLatitude,
                    longitude: formData.placeLongitude,
                    isPartner: !!formData.partnerPlaceId,
                    partnerId: formData.partnerPlaceId
                }
            } : undefined
        });
    };

    return (
        <CommonLayout
            title="모임 만들기"
            onBack={() => navigate('/app/meetings', { replace: true })}
        >
            <ConfirmModal
                isVisible={confirmState.isVisible}
                title="모임 만들기"
                message={'모임을 만드시겠습니까? \n쌀 500알이 차감됩니다.'}
                onConfirm={handleConfirmSubmit}
                onCancel={() => setConfirmState({ isVisible: false })}
            />
            <AlertModal
                isVisible={alertState.isVisible}
                message={alertState.message}
                callback={alertState.callback}
                onClose={() => setAlertState({ isVisible: false, message: '', callback: null })}
            />
            <div className="max-w-md mx-auto bg-white">
                <ImageUploadContainer
                    imageUrl={imageUrl}
                    onUpload={handleImageUpload}
                    onDelete={handleImageDelete}
                    isLoading={isLoading}
                />
                <div className="p-4 space-y-6">
                    {/* 제목 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">제목</label>
                        <input
                            type="text"
                            placeholder="모임 제목을 입력해주세요 (최대 20자)"
                            value={formData.title}
                            onChange={(e) => updateFormData('title', e.target.value)}
                            maxLength={20}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            disabled={isLoading}
                        />
                        <p className="text-xs text-gray-500 text-right mt-1">{formData.title.length}/20</p>
                    </div>

                    {/* 설명 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">설명</label>
                        <textarea
                            placeholder="모임에 대한 설명을 입력해주세요 (최대 300자)"
                            value={formData.content}
                            onChange={(e) => updateFormData('content', e.target.value)}
                            maxLength={300}
                            rows={4}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                            disabled={isLoading}
                        />
                        <p className="text-xs text-gray-500 text-right mt-1">{formData.content.length}/300</p>
                    </div>

                    {/* 모임 일시 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">모임 일시</label>
                        <div className="space-y-2">
                            <button
                                onClick={() => setShowDatePicker(true)}
                                className="w-full flex items-center p-3 border border-gray-300 rounded-lg active:bg-gray-50"
                                disabled={isLoading}
                            >
                                <IoCalendarOutline size={20} className="text-gray-400 mr-3" />
                                <span className={selectedDate ? 'text-gray-900' : 'text-gray-500'}>
                                    {formatDateDisplay(selectedDate)}
                                </span>
                            </button>
                            <button
                                onClick={() => setShowTimePicker(true)}
                                className="w-full flex items-center p-3 border border-gray-300 rounded-lg active:bg-gray-50"
                                disabled={isLoading}
                            >
                                <IoTimeOutline size={20} className="text-gray-400 mr-3" />
                                <span className={selectedTime ? 'text-gray-900' : 'text-gray-500'}>
                                    {formatTimeDisplay(selectedTime)}
                                </span>
                            </button>
                        </div>
                        {isPastDateTime() && (
                            <p className="text-sm text-red-500 mt-2">
                                현재 시간 이전은 선택할 수 없습니다
                            </p>
                        )}
                    </div>

                    {/* 모임 장소 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">모임 장소</label>
                        {formData.placeName ? (
                            <div className="space-y-3">
                                <div className="w-full aspect-[2/1] bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                                    <StaticKakaoMapContainer
                                        latitude={formData.placeLatitude}
                                        longitude={formData.placeLongitude}
                                        level={3}
                                        className="w-full h-full"
                                    />
                                </div>
                                <button
                                    onClick={handlePlaceSelect}
                                    className="w-full p-4 rounded-lg border transition-all duration-200"
                                    style={formData.partnerPlaceId ? {
                                        background: 'linear-gradient(135deg, rgba(0,31,63,0.05) 0%, rgba(0,61,122,0.08) 20%, rgba(0,116,217,0.1) 40%, rgba(57,204,204,0.08) 60%, rgba(46,204,64,0.06) 80%, rgba(61,153,112,0.05) 100%)',
                                        borderColor: 'rgba(0,116,217,0.2)'
                                    } : {
                                        backgroundColor: '#f9fafb',
                                        borderColor: '#e5e7eb'
                                    }}
                                    disabled={isLoading}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 text-left">
                                            <div className="flex items-center mb-1">
                                                {formData.partnerPlaceId && (
                                                    <span className="text-blue-500 text-sm drop-shadow-sm mr-1">✦</span>
                                                )}
                                                <span className="font-medium text-gray-900">{formData.placeName}</span>
                                            </div>
                                            <div className="text-sm text-gray-600 mb-1">{formData.placeAddress}</div>
                                            {formData.placePhone && (
                                                <div className="text-sm text-gray-500">{formData.placePhone}</div>
                                            )}
                                        </div>
                                        <IoChevronForward size={20} className="text-gray-400 ml-3" />
                                    </div>
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={handlePlaceSelect}
                                className="w-full aspect-[2/1] bg-gray-100 rounded-lg flex flex-col items-center justify-center active:bg-gray-200 transition-colors"
                                disabled={isLoading}
                            >
                                <IoLocationOutline size={32} className="text-gray-400 mb-2" />
                                <p className="text-gray-500 font-medium">장소 선택</p>
                            </button>
                        )}
                    </div>

                    {/* 최소 인원 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">최소 인원</label>
                        <div className="flex items-center justify-between p-3 border border-gray-300 rounded-lg">
                            <span className="text-gray-700">{formData.minCapacity}명</span>
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={() => handleMinCapacityChange(-1)}
                                    className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center active:bg-gray-300 disabled:opacity-50"
                                    disabled={formData.minCapacity <= 2 || isLoading}
                                >
                                    <IoRemove size={16} />
                                </button>
                                <button
                                    onClick={() => handleMinCapacityChange(1)}
                                    className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center active:bg-gray-300 disabled:opacity-50"
                                    disabled={formData.minCapacity >= formData.maxCapacity || isLoading}
                                >
                                    <IoAdd size={16} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* 최대 인원 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">최대 인원</label>
                        <div className="flex items-center justify-between p-3 border border-gray-300 rounded-lg">
                            <span className="text-gray-700">{formData.maxCapacity}명</span>
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={() => handleMaxCapacityChange(-1)}
                                    className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center active:bg-gray-300 disabled:opacity-50"
                                    disabled={formData.maxCapacity <= formData.minCapacity || isLoading}
                                >
                                    <IoRemove size={16} />
                                </button>
                                <button
                                    onClick={() => handleMaxCapacityChange(1)}
                                    className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center active:bg-gray-300 disabled:opacity-50"
                                    disabled={formData.maxCapacity >= 20 || isLoading}
                                >
                                    <IoAdd size={16} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* 승인 여부 */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">모임장 승인 필요 여부</label>
                        <div className="flex items-center justify-between p-3 border border-gray-300 rounded-lg">
                            <div className="flex flex-col">
                                <span className="text-gray-900 font-medium">
                                    {formData.requiresApproval ? '승인 필요' : '승인 없이 가입'}
                                </span>
                                <span className="text-sm text-gray-500">
                                    {formData.requiresApproval
                                        ? '모임장이 가입 신청을 승인해야 합니다'
                                        : '누구나 바로 가입할 수 있습니다'}
                                </span>
                            </div>
                            <button
                                onClick={() => updateFormData('requiresApproval', !formData.requiresApproval)}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                    formData.requiresApproval ? 'bg-amber-500' : 'bg-gray-200'
                                }`}
                                disabled={isLoading}
                            >
                                <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                        formData.requiresApproval ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                                />
                            </button>
                        </div>
                    </div>

                    <div className="p-4 bg-gray-100 rounded-md">
                        <ul className="list-disc pl-5 text-black text-sm">
                            <li>모임 생성 후 일시, 최소 인원, 최대 인원, 모임장 승인 필요 여부는 수정할 수 없습니다.</li>
                        </ul>
                    </div>

                    <div className="h-20"></div>
                </div>

                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
                    <div className="max-w-md mx-auto">
                        <button
                            onClick={handleSubmit}
                            disabled={!isFormValid() || isLoading}
                            className={`w-full py-4 rounded-lg font-medium ${
                                isFormValid() && !isLoading
                                    ? 'bg-amber-500 text-white active:bg-amber-600'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            {isLoading ? '로딩 중...' : '모임 만들기'}
                        </button>
                    </div>
                </div>

                <DatePickOverlay
                    isVisible={showDatePicker}
                    onClose={() => setShowDatePicker(false)}
                    onSelect={setSelectedDate}
                    initialDate={selectedDate}
                />

                <TimePickOverlay
                    isVisible={showTimePicker}
                    onClose={() => setShowTimePicker(false)}
                    onSelect={setSelectedTime}
                    initialTime={selectedTime}
                />
            </div>
        </CommonLayout>
    );
};

export default CreateMeetingPage;
